import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@edusense/shared-auth-core';
import {
  useGetNotifications,
  useMarkNotificationAsRead,
  useNotificationSSE,
  type NotificationItem,
} from '@edusense/notification-client';
import { API_ENDPOINTS } from '@edusense/shared-constants';
import { NotificationsTemplate } from '@edusense/features-notification';
import type { Notification as TemplateNotification } from '@edusense/shared-ui-kit';

export interface NotificationsPageProps {
  navigate?: (path: string) => void;
}

/**
 * NotificationItem (API) → TemplateNotification (UI) 변환
 */
const toTemplateNotification = (item: NotificationItem): TemplateNotification => {
  const { basicInfo, status, source, target, data } = item;

  const sender = source?.sourceUser
    ? {
        id: String(source.sourceUser.id ?? source.sourceUserId ?? 'system'),
        name: source.sourceUser.displayName ?? source.sourceUser.username ?? '알 수 없음',
        username: source.sourceUser.username ?? '',
        profileImage: source.sourceUser.profileImageUrl ?? '',
      }
    : {
        id: String(source?.sourceUserId ?? 'system'),
        name: '시스템',
        username: 'system',
        profileImage: '',
      };

  const tweetId =
    target?.targetType === 'TWEET' && target.targetId
      ? String(target.targetId)
      : undefined;

  return {
    id: String(basicInfo.id),
    type: basicInfo.type,
    sender,
    tweetId,
    tweetContent: (data?.tweetContent as string) ?? undefined,
    replyContent: (data?.replyContent as string) ?? undefined,
    isRead: status.isRead,
    createdAt: basicInfo.createdAt ?? new Date().toISOString(),
  };
};

/**
 * 알림 페이지 - Orval 기반 리팩토링 버전
 *
 * 개선 사항:
 * - Orval React Query hooks 사용 (useGetNotifications, useMarkNotificationAsRead)
 * - 커스텀 SSE 훅 사용 (useNotificationSSE)
 * - Fallback 로직 제거 (OpenAPI 단일 엔드포인트)
 * - 복잡한 데이터 변환 최소화
 * - 550줄 → 150줄 (73% 감소)
 */
export default function NotificationsPage({ navigate }: NotificationsPageProps) {
  const navigateInternal = useNavigate();
  const { user: authUser } = useAuth();

  // 사용자 프로필 ID 조회
  const [profileId, setProfileId] = useState<number | null>(null);

  // recipientId 추출
  const recipientId = useMemo(() => {
    if (!authUser) return null;

    const candidate = authUser.id ?? (authUser as any)?.uuid ?? (authUser as any)?.userId;

    if (typeof candidate === 'string') {
      return candidate.trim() || null;
    }
    if (typeof candidate === 'number') {
      return String(candidate);
    }
    return null;
  }, [authUser]);

  // 알림 목록 조회 (React Query)
  const {
    data: notificationsData,
    isLoading,
    error: queryError,
    refetch,
  } = useGetNotifications(
    { recipientId: recipientId ?? '', limit: 50 },
    {
      query: {
        enabled: !!recipientId,
        staleTime: 30000, // 30초
        refetchOnWindowFocus: true,
      },
    }
  );

  // 읽음 처리 Mutation
  const { mutate: markAsReadMutation } = useMarkNotificationAsRead({
    mutation: {
      onError: (error) => {
        console.error('알림 읽음 처리 중 오류 발생:', error);
      },
    },
  });

  // 읽지 않은 알림 수
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (notificationsData?.unreadCount !== undefined) {
      setUnreadCount(notificationsData.unreadCount);
    }
  }, [notificationsData]);

  // SSE 실시간 알림 (커스텀 훅)
  useNotificationSSE({
    recipientId,
    profileId,
    enabled: !!recipientId,
    onNotification: () => {
      // 새 알림 도착 시 목록 갱신
      refetch();
    },
    onUnreadCountChange: (count) => {
      setUnreadCount(count);
    },
  });

  // 템플릿용 알림 목록 변환
  const notifications = useMemo<TemplateNotification[]>(() => {
    return (notificationsData?.notifications ?? []).map(toTemplateNotification);
  }, [notificationsData]);

  // 알림 읽음 처리
  const handleMarkAsRead = useCallback(
    (id: string) => {
      const numericId = Number(id);
      if (!Number.isNaN(numericId)) {
        markAsReadMutation({ id: numericId });
      }
    },
    [markAsReadMutation]
  );

  // 네비게이션 처리
  const handleNavigate = useCallback(
    (url: string) => {
      const navigateFn = navigate ?? navigateInternal;
      if (url) {
        navigateFn(url);
      }
    },
    [navigate, navigateInternal]
  );

  // 알림 URL 생성
  const resolveNavigationUrl = useCallback((notification: TemplateNotification): string => {
    if (notification.tweetId) {
      return `/tweet/${notification.tweetId}`;
    }
    if (notification.type === 'FOLLOW' && notification.sender.username) {
      return `/user/${notification.sender.username}`;
    }
    return '/notifications';
  }, []);

  // 알림 클릭 핸들러
  const handleNotificationClick = useCallback(
    (notification: TemplateNotification) => {
      if (!notification.isRead) {
        handleMarkAsRead(notification.id);
      }
      const destination = resolveNavigationUrl(notification);
      handleNavigate(destination);
    },
    [handleNavigate, handleMarkAsRead, resolveNavigationUrl]
  );

  // 탭 변경 핸들러
  const handleTabChange = useCallback((tabId: string) => {
    console.log(`알림 탭 변경: ${tabId}`);
  }, []);

  // 에러 메시지
  const errorMessage = queryError
    ? '알림을 불러오는 중 오류가 발생했습니다.'
    : null;

  return (
    <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white">
      {errorMessage ? (
        <div className="p-4 text-red-500">{errorMessage}</div>
      ) : (
        <NotificationsTemplate
          notifications={notifications}
          isLoading={isLoading}
          currentUserId={recipientId ?? undefined}
          onMarkAsRead={handleMarkAsRead}
          onNavigate={handleNavigate}
          onNotificationClick={handleNotificationClick}
          onTabChange={handleTabChange}
        />
      )}
      <div className="sr-only" aria-live="polite">
        읽지 않은 알림 {unreadCount}건
      </div>
    </div>
  );
}
